import {
  DiscussionAndMessages,
  DiscussionContext,
  DiscussionContextType,
  Message,
} from "@cnx/common";
import { useChatDiscussionContext } from "hooks/useChatDiscussionContext";
import { useDiscussion } from "hooks/useDiscussions";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useChatPanelStore } from "stores/chatPanel";
import { getDraftTitle } from "utils/chat";

interface ChatContextProviderProps {
  children: React.ReactNode;
}

type ChatContextStore = {
  title?: string;
  discussionContextType?: DiscussionContextType;
  discussionId?: string;
  discussion?: DiscussionAndMessages;
  discussionIsPending: boolean;
  isChatLoading: boolean;
  discussionContext?: DiscussionContext;
  contextId?: string;
  editingMessage: Message | null;
  setEditingMessage: (message: Message | null) => void;
  selectedMessages: Message[];
  setSelectedMessages: React.Dispatch<React.SetStateAction<Message[]>>;
};

const ChatContext = createContext<undefined | ChatContextStore>(undefined);

export const useChatContextStore = () => {
  const store = useContext(ChatContext);

  if (!store) {
    throw new Error(
      "useChatContextStore must be used within a ChatContextProvider"
    );
  }

  return store;
};

const ChatContextProvider: React.FC<ChatContextProviderProps> = ({
  children,
}) => {
  const panelDiscussionId = useChatPanelStore(store => store.panelDiscussionId);
  const panelContextId = useChatPanelStore(store => store.panelContextId);
  const discussionContextType = useChatPanelStore(store => store.panelContext);

  const {
    discussion,
    isPending: discussionIsPending,
    isFetching: discussionIsFetching,
  } = useDiscussion({
    discussionId: panelDiscussionId ?? undefined,
  });

  const resolvedDiscussionContextType =
    discussionContextType ?? discussion?.context ?? undefined;

  const { discussionContext, isFetching: discussionContextIsFetching } =
    useChatDiscussionContext({
      contextId: panelContextId ?? undefined,
      context: resolvedDiscussionContextType,
    });

  const title = discussion?.title ?? getDraftTitle(discussionContext);
  const isChatLoading = discussionIsFetching || discussionContextIsFetching;

  const [editingMessage, setEditingMessage] = useState<Message | null>(null);
  const [selectedMessages, setSelectedMessages] = useState<Message[]>([]);

  useEffect(() => {
    return () => {
      setEditingMessage(null);
      setSelectedMessages([]);
    };
  }, [panelDiscussionId, panelContextId]);

  const value = useMemo(
    () => ({
      discussion,
      discussionIsPending,
      isChatLoading,
      discussionContext,
      title,
      discussionContextType: resolvedDiscussionContextType,
      discussionId: panelDiscussionId ?? undefined,
      contextId: panelContextId ?? undefined,
      editingMessage,
      setEditingMessage,
      selectedMessages,
      setSelectedMessages,
    }),
    [
      discussion,
      discussionIsPending,
      isChatLoading,
      discussionContext,
      title,
      resolvedDiscussionContextType,
      panelDiscussionId,
      panelContextId,
      editingMessage,
      selectedMessages,
    ]
  );
  return <ChatContext value={value}>{children}</ChatContext>;
};

export default ChatContextProvider;
